import React, { useEffect, useRef, useState } from 'react';

const PrincipalMessage = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id="principal"
      className="py-20 sm:py-24 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #0f172a 100%)' }}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }}
      />
      <div className="absolute top-0 left-0 w-72 h-72 rounded-full bg-primary/20 blur-3xl -z-0" />
      <div className="absolute bottom-0 right-0 w-56 h-56 rounded-full bg-accent/15 blur-3xl -z-0" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-inter mb-4">
            <i className="fas fa-quote-left text-primary" />
            Message from Leadership
          </div>
          <h2 className="font-poppins font-800 text-white mb-4" style={{ fontSize: 'clamp(26px, 4vw, 40px)' }}>
            A Word from Our
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent"> Regional Head</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-center">
          {/* Image */}
          <div className={`lg:col-span-2 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="relative max-w-sm mx-auto lg:mx-0">
              {/* Decorative ring */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary to-accent p-0.5 -m-1">
                <div className="w-full h-full rounded-2xl bg-navy" />
              </div>
              <div className="relative rounded-2xl overflow-hidden aspect-[4/5]">
                <img
                  src="/assets/principal-image.jpeg"
                  alt="Syed Muhammad Ilyas - Regional Head, PAK School"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
                {/* Badge */}
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-navy/90 via-navy/60 to-transparent p-6">
                  <div className="font-poppins font-700 text-white text-lg leading-tight">Syed Muhammad Ilyas</div>
                  <div className="text-primary text-sm mt-1 font-inter">Regional Head</div>
                </div>
              </div>
            </div>
          </div>

          {/* Message */}
          <div className={`lg:col-span-3 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            {/* Large quote mark */}
            <div className="text-primary/30 font-poppins leading-none mb-6" style={{ fontSize: '80px', lineHeight: 1 }}>
              "
            </div>

            <div className="space-y-4 mb-8">
              <p className="font-poppins font-600 text-xl sm:text-2xl text-white leading-relaxed">
                Welcome To PAK School &amp; College Mardan!
              </p>
              <p className="text-gray-300 font-inter text-base leading-relaxed">
                Our community is dedicated to academic excellence, personal growth, and the pursuit of knowledge. We prioritize student-centered learning, fostering an environment that encourages 21st century skills like curiosity, critical thinking, and collaboration.
              </p>
              <p className="text-gray-300 font-inter text-base leading-relaxed">
                Our team of educators is committed to providing a supportive and inclusive atmosphere where each student can reach their full potential. Through innovative teaching methods and a comprehensive curriculum, we prepare our students for success in an ever-evolving world.
              </p>
              <p className="text-gray-400 font-inter text-base leading-relaxed">
                Let's inspire a passion for learning, cultivate resilience, and empower students on their journey of lifelong achievement.
              </p>
            </div>

            {/* Signature */}
            <div className="flex items-center gap-4 pt-6 border-t border-white/10">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center font-poppins font-700 text-white text-lg flex-shrink-0">
                S
              </div>
              <div>
                <div className="font-poppins font-700 text-white">Syed Muhammad Ilyas</div>
                <div className="text-primary text-sm font-inter">Regional Head, PAK School &amp; College System</div>
              </div>
            </div>

            {/* Values chips */}
            <div className="flex flex-wrap gap-2 mt-8">
              {['Academic Excellence', 'Character Building', 'Student-Centered', '21st Century Skills'].map((v) => (
                <span key={v} className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/80 text-xs font-inter">
                  {v}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PrincipalMessage;
